import isEqual from 'lodash/isEqual';
import type React from 'react';
import { createContext, useReducer, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { usePrevious } from 'react-use';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { routeFor, routes } from 'src/core/constants/routes';
import { LocalStorageKey } from 'src/core/constants/storage';
import { getLocalItem } from 'src/core/utils/storage';

import { filtersToUrlQueryString } from './filtersToUrlQueryString';
import { urlQueryStringToFilters } from './urlQueryStringToFilters';
import {
  type Filters as State,
  type SortFilter,
  type GroupFilter,
  defaultSortFilter,
  defaultGroupFilter,
} from '../../models';

const getDefaultState = (): State => {
  const preferredView = getLocalItem(LocalStorageKey.BookkeepView, true);
  if (preferredView === 'table') {
    return {
      sort: defaultSortFilter,
      group: undefined,
    };
  }
  return {
    sort: defaultSortFilter,
    group: defaultGroupFilter,
  };
};

export const getFiltersFromUrl = (location: Location): State =>
  urlQueryStringToFilters(getDefaultState(), location.search);

type Action =
  | { type: 'SET_SORT'; sort: SortFilter }
  | { type: 'SET_GROUP'; group: GroupFilter }
  | { type: 'SET_SEARCH'; search: string }
  | { type: 'RESET_FILTERS' };

const getInitialState = (
  filtersFromUrl: ReturnType<typeof getFiltersFromUrl>,
): State => {
  let sort: SortFilter = filtersFromUrl.sort || defaultSortFilter;

  if (filtersFromUrl.group) {
    sort = 'date-asc';
  }

  return {
    sort,
    group: filtersFromUrl.group,
    search: filtersFromUrl.search,
  };
};

export const usePreparePayablesFiltersContextReducer = (): [
  State,
  React.Dispatch<Action>,
] => {
  const history = useHistory();
  const companyId = useCompanyId();

  const reducer = (state: State, action: Action): State => {
    switch (action.type) {
      case 'SET_SORT': {
        return { ...state, sort: action.sort };
      }
      case 'SET_GROUP': {
        return {
          ...state,
          group: state.group === action.group ? undefined : action.group,
        };
      }
      case 'SET_SEARCH': {
        return { ...state, search: action.search };
      }
      case 'RESET_FILTERS': {
        return { ...getDefaultState(), search: '' };
      }
      default:
        throw new Error(`Unexpected action ${action}`);
    }
  };

  const [state, dispatch] = useReducer(
    reducer,
    getInitialState(getFiltersFromUrl(history.location)),
  );

  const previousState = usePrevious(state);

  // Navigate back to the index page if filters has changed but we are not on first render
  useEffect(() => {
    if (previousState && !isEqual(previousState, state)) {
      history.replace({
        pathname: routeFor(routes.EXPENSE_INBOX_PREPARE.path, {
          company: companyId,
        }),
        search: `?${filtersToUrlQueryString(state, window.location.search)}`,
      });
    }
  }, [state]);

  return [state, dispatch];
};

export const actionsFactory = (dispatch: (action: Action) => void) => ({
  setSortFilter: (sort: SortFilter): void => {
    dispatch({ type: 'SET_SORT', sort });
  },
  setGroupFilter: (group: GroupFilter): void => {
    dispatch({ type: 'SET_GROUP', group });
  },
  setSearch: (search: string): void => {
    dispatch({ type: 'SET_SEARCH', search });
  },
  reset: (): void => {
    dispatch({ type: 'RESET_FILTERS' });
  },
});

export const PreparePayablesFiltersContext: React.Context<{
  state: State;
  actions: ReturnType<typeof actionsFactory>;
}> = createContext({
  state: getDefaultState(),
  actions: actionsFactory(() => {}),
});
